import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { action, observable } from 'mobx';

import ScrollToTop from 'lib/scroll/scroll-to-top';
import Styled from 'styled';
import { InterscrollerPlaceholder } from 'lib/google/dfp/ad-units/interscroller';

import { Site } from 'four-nets/site';
import PagerView from 'four-nets/ui/pager-view';
import Newsletter from 'four-nets/newsletter/newsletter';
import ErrorPage from 'four-nets/errors/error-page';

import BlogPostView, { BlogPostViewModel } from './blog-post-view';
import defaultStyles from './blog-posts-view.scss';

export class BlogPostsViewModel {
    @observable posts: PagedResult<Post>;
    @observable blogPostViewModels: BlogPostViewModel[] = [];
    @observable error: Error = null;

    constructor(dataSource, category, page, perPage) {
        this.category = category;
        this.page = page;
        this.perPage = perPage;
        this.dataSource = dataSource;
    }

    @action loadDidSucceed = posts => {
        this.posts = posts;
        this.blogPostViewModels.replace(posts.results.map(post => new BlogPostViewModel(post)));
    };

    @action loadDidFail = error => {
        this.posts = null;
        this.blogPostViewModels.replace([]);
        this.error = error;
    };

    toJSON() {
        return {
            posts: this.posts,
        };
    }

    fromJSON(json) {
        this.loadDidSucceed(this.dataSource.postStore.processPagedJSON({ json: json.posts }));
    }

    load() {
        return this.dataSource.postStore
            .loadAll({
                query: {
                    categories: (this.category && this.category.id) || undefined,
                    page: this.page || undefined,
                    per_page: this.perPage || undefined,
                    fields: JSON.stringify([
                        'date',
                        'date_gmt',
                        'title',
                        'comments_count',
                        'read_count',
                        'liker_ids',
                        ['categories', ['name', 'slug']],
                        'slug',
                        'excerpt',
                        ['featured_media', ['media_details', 'alt_text', 'source_url', 'guid']],
                    ]),
                },
            })
            .then(this.loadDidSucceed)
            .catch(this.loadDidFail);
    }
}

@Styled(defaultStyles)
@observer
class BlogPostsView extends React.Component {
    static defaultProps = {
        withPager: true,
        firstMaxi: false,
        hompage: false,
    };

    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    render() {
        const { blogPostsViewModel, firstMaxi, styles, withPager, homepage } = this.props;
        const { site } = this.context;
        const { blogPostViewModels, posts, error } = blogPostsViewModel;

        return (
            <ErrorPage error={error}>
                <div className={styles.blogPost}>
                    <ScrollToTop blogPostViewModels={blogPostViewModels} />
                    {blogPostViewModels.length === 0 ? (
                        <p>
                            <FormattedMessage id='THERE_IS_NO_ITEMS_IN_THIS_CATEGORY' />
                        </p>
                    ) : null}
                    {blogPostViewModels.map((blogPostViewModel, index) => (
                        <div key={index}>
                            {index === 3 && site.isGarden ? <Newsletter /> : null}
                            {!homepage && index === 5 ? <InterscrollerPlaceholder /> : null}
                            <BlogPostView
                                maxi={firstMaxi && index === 0}
                                key={blogPostViewModel.post.id}
                                blogPostViewModel={blogPostViewModel}
                            />
                        </div>
                    ))}
                    {withPager && posts ? (
                        <div className={styles.pager}>
                            <PagerView count={posts.pages} current={posts.page} />
                        </div>
                    ) : null}
                </div>
            </ErrorPage>
        );
    }
}

export default BlogPostsView;
